import { useMemo, useState } from 'react';
import PreviewFrame from '../components/PreviewFrame.tsx';
import CommandBar from '../canvas/CommandBar.tsx';
import Filmstrip from '../canvas/Filmstrip.tsx';
import AddSlideChooser from '../canvas/AddSlideChooser.tsx';
import Toast from '../components/Toast.tsx';
import { slideScope } from '../lib/scope.ts';
import { useChatStream } from '../lib/useChatStream.ts';
import { useDeckEditor } from '../editor/useDeckEditor.ts';
import { ChevronLeft } from '../lib/icons.tsx';
import { geometryAsk } from '../lib/geometryAsk.ts';
import { useGeometryBadgeFix } from '../lib/useGeometryBadgeFix.ts';
import type { LibraryItem, LibrarySlide } from '../../shared/api.ts';
import type { SlideEntry } from '../../shared/deck.ts';
import type { NewSection } from '../editor/useDeckEditor.ts';

type SlideEditorOverlayProps = {
  item: LibraryItem;
  /** The item's first slide, as the LIBRARY lists it. Deliberately not a
   *  `SlideEntry`: it stands in only until the deck summary lands. */
  slide: LibrarySlide;
  onClose: () => void;
};

/**
 * Click-to-edit for one library ITEM: a full-viewport overlay running the SAME
 * editor core as the deck editor (editor/useDeckEditor.ts) — the rail, drag
 * reorder, duplicate/delete/move, section rename/recolour/create/delete/move,
 * the Add-a-slide gallery (template / library / upload picks — no
 * describe-in-chat here), and chat per slide.
 *
 * The item owns its deck, so the editor simply opens it. There is no filtering,
 * no shadow index and no merge back into a larger list: until 2026-08-21 all of
 * that existed to make one section of a shared shelf deck look like a
 * self-contained thing, and it leaked anyway — the rail read "07 · Morgan
 * Sindall", a position in a running order nobody was being shown.
 *
 * Scope stays pinned to the focused slide — both scope-switch callbacks are
 * null, which also hides the slide/whole-deck toggle (CommandBar guards on it).
 * The overlay closes itself when the item's last section is deleted.
 */
export default function SlideEditorOverlay({ item, slide, onClose }: SlideEditorOverlayProps) {
  const slug = item.slug;
  const [convOpen, setConvOpen] = useState(true);
  /** One-shot composer seed, for the geometry badge below. */
  const [prefill, setPrefill] = useState<string | null>(null);

  const ed = useDeckEditor({
    slug,
    initialSlideId: slide?.id || null,
    onEmptied: onClose,
    // An item is a shelf entry, not a presentation: it carries exactly its
    // curated slides, never a SectionDivider stub.
    noDividers: true,
  });
  const { slides, sections, mutate, addChooser, setAddChooser, sectionName } = ed;

  // The red geometry badge, answered here as well as in the deck editor. It
  // says "click to fix", and on this surface it used to do nothing at all: the
  // handler lived inline in routes/Canvas.tsx, so the badge on a library item
  // posted a message with no listener (Alex, 2026-08-20).
  //
  // No selection to clear and no scope to widen — this overlay is always scoped
  // to ONE slide — so it only has to focus the right one and seed the composer.
  useGeometryBadgeFix(({ idx, kind, detail }) => {
    setConvOpen(true);
    const target = idx >= 0 ? slides[idx] : null;
    if (target) ed.jumpToSlide(target.id);
    setPrefill(geometryAsk({ kind, detail }));
  });

  // Before the summary lands there is no ViewSlide yet, so the card's library
  // row stands in. `slideScope` reads only `id` and `title`, and the real
  // slide replaces this on the very next render — but the scope has to exist
  // now, or the chat opens on nothing and re-keys a moment later.
  const focused: SlideEntry = ed.activeSlide ?? {
    idx: slide.idx,
    id: slide.id,
    section: slide.section,
    title: slide.title,
    file: '',
  };
  // Keyed on the id, not the object: a fresh summary hands back a new `focused`
  // every poll, and re-scoping on that would restart the chat stream.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const scope = useMemo(() => slideScope(slug, focused), [slug, focused.id]);
  const chat = useChatStream(scope);

  // The deck's title is the item's name, whether it is split or not.
  const itemTitle = item.name;

  /**
   * The rail's sections, with the ordinal dropped from a lone one.
   *
   * A single-section item IS the item, and the header above already names it,
   * so "01 · Morgan Sindall" is a number counting to one. A split item keeps
   * 01, 02… because there the ordinal is real internal structure.
   *
   * Done here rather than in Filmstrip/SectionHeader so ordinary decks are
   * untouched by construction: they render `{section.n ? … }` exactly as
   * before, and `SectionRef.n` is already nullable.
   */
  const railSections = useMemo(
    () => (sections.length === 1 ? sections.map((s) => ({ ...s, n: null })) : sections),
    [sections],
  );

  // A brand-new section is invisible in the rail until it has a slide
  // (no divider stubs in a bank), so go straight into picking one.
  async function handleCreateSection(args: NewSection) {
    const key = await ed.handleCreateSection(args);
    if (key) ed.handleAddSlide(key);
  }

  const chatEmpty = (
    <div className="conversation__empty">
      <p>
        Ask for changes to <strong>{scope.label}</strong>: this edits the library copy every future insert starts from.
      </p>
      <p className="muted small">
        Decks that already use this slide keep their own snapshot; nothing you do here touches them.
      </p>
    </div>
  );

  return (
    <div className="library-editor" role="dialog" aria-label={`Edit ${itemTitle || 'item'}`}>
      <header className="editor__topbar">
        <div className="editor__topbar-left">
          <button
            type="button"
            className="iconbtn"
            onClick={onClose}
            title="Back to library"
            aria-label="Back to library"
          >
            <ChevronLeft />
          </button>
          <span className="editor__deck-name">{itemTitle || item.slug}</span>
          <span className="editor__deck-meta muted small">
            {slides.length > 0 ? `${slides.length} slide${slides.length === 1 ? '' : 's'} · ` : ''}RKH Library
          </span>
        </div>
      </header>
      {ed.status === 'error' && (
        <div className="editor__error">
          <strong>Could not open the item</strong>
          <p className="muted">{ed.error}</p>
        </div>
      )}
      {ed.status !== 'error' && (
        <div className="editor__stage library-editor__stage">
          <Filmstrip
            slug={slug}
            sections={railSections}
            slides={slides}
            activeIdx={ed.activeIdx}
            onSelect={ed.selectSlide}
            onAddSlide={ed.handleAddSlide}
            onReorderCommit={ed.commitReorder}
            onDuplicate={ed.handleDuplicate}
            onToggleHidden={ed.handleToggleHidden}
            onMoveToSection={ed.handleMoveToSection}
            onSaveToBank={null}
            onDeleteSlide={ed.handleDeleteSlide}
            onRenameSection={ed.handleRenameSection}
            onRecolourSection={ed.handleRecolourSection}
            onDeleteSection={ed.handleDeleteSection}
            onCreateSection={handleCreateSection}
            onMoveSection={ed.handleMoveSection}
            busy={mutate.busy}
          />
          <PreviewFrame
            slug={slug}
            // This surface answers the badge — see the hook below. Without it
            // the badge offered "click to fix" here and nothing happened.
            canFix
            // No deck chrome. A library slide is not page 3 of anything, and
            // the label it would carry describes the deck it happens to live
            // in rather than the deck it is destined for — which is why the
            // tiles and the zoom modal have always stripped it too.
            chrome={false}
            activeIdx={ed.activeIdx ?? slide.idx ?? 0}
            onActiveChange={ed.handlePreviewActiveChange}
            reloadKey={ed.previewReloadKey}
          />
          <CommandBar
            scope={scope}
            chat={chat}
            open={convOpen}
            onToggleOpen={setConvOpen}
            prefill={prefill}
            onPrefillConsumed={() => setPrefill(null)}
            onClearScope={null}
            onSelectSlideScope={null}
            slides={slides}
            onJumpToSlide={ed.jumpToSlide}
            onApplied={ed.bumpPreviewReload}
            empty={chatEmpty}
            sections={sections}
            onSlideEdited={ed.bumpPreviewReload}
            onToggleLayoutOk={ed.handleToggleLayoutOk}
            forceTab={ed.forceEditTab ? 'edit' : null}
            onForceTabConsumed={ed.clearForceEditTab}
          />
        </div>
      )}

      <Toast toast={ed.toast.toast} onUndo={ed.toast.runUndo} onDismiss={ed.toast.dismissToast} />

      {addChooser && (
        <AddSlideChooser
          where={
            addChooser.refSlide
              ? `after "${addChooser.refSlide.title}"`
              : addChooser.sectionKey
                ? `to ${sectionName(addChooser.sectionKey)}`
                : `to "${itemTitle || item.slug}"`
          }
          deckSlug={slug}
          onDescribe={null}
          onPickTemplate={ed.handleAddFromTemplate}
          onUploadSlide={ed.handleUploadSlide}
          onClose={() => setAddChooser(null)}
          busy={mutate.busy}
        />
      )}
    </div>
  );
}
